import {
  useCallback, useEffect, useMemo, useState, MouseEvent,
} from 'react';
import { Checkbox, Empty } from 'antd';
import { arrayMove } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { DraggableOption, DraggableContext, Option } from 'shared/dnd';
import { DragEndEvent } from '@dnd-kit/core';
import { StyledSelectWrapper, StyledDraggableOptionSelectAll, StyledDraggableSelectWrapper } from 'components/styledComponents/select/Select.styled';
import type { DefaultOptionType } from 'antd/es/select';
import { AdminMultiProps, IdType, SelectOptionProps } from './AdminSelect';

const AdminOrderedMultiSelect = <OptionType extends SelectOptionProps, IsToString extends boolean = true>({
  id = '',
  data,
  isToString = true as IsToString,
  onChange: onChangeProps,
  value,
  isSelectAll = true,
  listHeight = 256,
  searchType = 'startsWith',
  renderOptions,
  ...rest
}: AdminMultiProps<OptionType, IsToString> & { listHeight?: number }) => {
  const { t } = useTranslation();

  type Data = IdType<OptionType, IsToString>;

  const [options, setOptions] = useState<OptionType[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const matchSearch = useCallback((input: string, name: string = '') => name.toString().toLowerCase()[searchType](input.toLowerCase()), [searchType]);

  const filterOption = (input: string, option?: DefaultOptionType) => {
    if (option?.value === 'all') {
      return !!data?.find((el) => matchSearch(input, el.name));
    }
    return matchSearch(input, String(option?.children ?? ''));
  };

  const filterState = useMemo(
    () => options.filter((el) => {
      if (!searchValue) return true;
      return matchSearch(searchValue, typeof el.displayName === 'string' ? el.displayName || el.name : el.name);
    }),
    [matchSearch, options, searchValue],
  );

  const getOptionsIds = useCallback((opts: OptionType[]|undefined) => opts?.map((el) => (isToString ? el.id.toString() : (el.id as Data))) ?? [], [isToString]);

  const all: Data[] = useMemo(() => getOptionsIds(data), [getOptionsIds, data]);
  const filterStateIds: Data[] = useMemo(() => getOptionsIds(filterState), [getOptionsIds, filterState]);

  const intersect = useMemo(() => (isSelectAll && filterStateIds.length ? _.intersection(value, filterStateIds) : all), [all, filterStateIds, isSelectAll, value]);
  const unique = useMemo(() => (isSelectAll && filterState.length ? _.union(value, filterStateIds) : all), [all, filterState.length, filterStateIds, isSelectAll, value]);

  const orderBy = useCallback(
    (array: OptionType[], order: Data[]) => {
      const selectedIndexMap = new Map();
      (id ? order : order.map(String)).forEach((val, index) => {
        selectedIndexMap.set(val.toString(), index);
      });

      return selectedIndexMap.size
        ? array.toSorted((a, b) => {
          const aID = a.id.toString();
          const bID = b.id.toString();
          const aSelected = selectedIndexMap.has(aID);
          const bSelected = selectedIndexMap.has(bID);
          if (aSelected && !bSelected) {
            return -1;
          }
          if (!aSelected && bSelected) {
            return 1;
          }
          if (aSelected && bSelected) {
            const aIndex = selectedIndexMap.get(aID);
            const bIndex = selectedIndexMap.get(bID);
            return aIndex - bIndex;
          }
          return 0;
        })
        : array;
    },
    [id],
  );

  const getSelectedIds = useCallback(
    (idValues: (Data | 'all')[]) => {
      if (isSelectAll && idValues?.includes('all')) {
        if (idValues.length === all.length + 1 && intersect.length === all.length) {
          return [];
        }

        if (!_.isEmpty(filterStateIds)) {
          if (value?.length && value.length === unique.length) {
            return _.difference(value, filterStateIds);
          }
          return unique;
        }

        return all;
      }
      return idValues;
    },
    [all, filterStateIds, intersect.length, isSelectAll, unique, value],
  );

  const onChange = useCallback(
    (values:(Data | 'all')[]) => {
      onChangeProps?.(getSelectedIds(values));
      setSearchValue('');
    },
    [getSelectedIds, onChangeProps],
  );

  useEffect(() => {
    if (data && value) {
      setOptions(orderBy(data, value));
    }
  }, [data, orderBy, value]);

  useEffect(
    () => () => {
      setSearchValue('');
    },
    [],
  );

  const handleSelectOption = useCallback(
    (idToToggle: number|string, e: MouseEvent) => {
      let newValue;
      const toggleId: Data = isToString ? idToToggle.toString() : +idToToggle as Data;
      if (value?.includes(toggleId)) {
        newValue = value.filter((v) => v !== toggleId);
      } else {
        newValue = [...(value || []), toggleId];
      }
      onChange(newValue as Data[]);
      e.stopPropagation();
    },
    [isToString, value, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!_.isUndefined(over?.id) && active.id !== over?.id) {
        const oldIndex = options.findIndex((el) => el.id.toString() === active.id.toString());
        const newIndex = options.findIndex((el) => el.id.toString() === over.id.toString());
        const newOptions = arrayMove(options, oldIndex, newIndex);
        setOptions(newOptions);
        onChange(_.intersection(getOptionsIds(newOptions), value));
      }
    },
    [getOptionsIds, onChange, options, value],
  );

  const handleSelectAll = () => {
    const checked = !(intersect?.length && filterStateIds.length === intersect?.length);
    if (checked) {
      onChange(filterStateIds);
    } else {
      onChange(_.difference(value, filterStateIds));
    }
  };

  const ids = useMemo(() => filterStateIds.map(String), [filterStateIds]);

  const popupRender = () => (
    <DraggableContext dragEnd={handleDragEnd} items={ids}>
      {filterState.length ? (
        <StyledDraggableSelectWrapper $listHeight={listHeight}>
          <StyledDraggableOptionSelectAll $isSelected={!!intersect?.length && filterStateIds.length === intersect?.length} onClick={handleSelectAll}>
            <Checkbox checked={!!intersect?.length && filterStateIds.length === intersect?.length} indeterminate={!!filterStateIds.length && !!value?.length && intersect?.length < filterStateIds.length}>
              <span>{t('selectAll')}</span>
            </Checkbox>
          </StyledDraggableOptionSelectAll>
          {filterState.map((el) => {
            const currId: Data = isToString ? el.id.toString() : el.id as Data;
            const isSelected = value?.includes(currId);
            const currIdStr = currId.toString();

            return (
              <DraggableOption key={currIdStr} id={currIdStr} isDraggable={!!isSelected}>
                <Option
                  id={currIdStr}
                  isSelected={isSelected}
                  onClick={handleSelectOption}
                  data={el}
                  isDraggable={isSelected}
                />
              </DraggableOption>
            );
          })}
        </StyledDraggableSelectWrapper>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('noData')} />
      )}
    </DraggableContext>
  );

  return (
    <StyledSelectWrapper
      mode="multiple"
      showSearch={{
        filterOption,
        onSearch: setSearchValue,
      }}
      value={value}
      popupRender={popupRender}
      onChange={onChange}
      classNames={{
        popup: {
          root: 'multi-select',
        },
      }}
      {...rest}
    >
      {renderOptions?.(options)}
    </StyledSelectWrapper>
  );
};

export default AdminOrderedMultiSelect;
